import Swiper from 'swiper';
import { SwiperEvents, SwiperModule } from 'swiper/types';

export interface MarqueeMethods {
    /**
     * Whether marquee enabled and running
     */
    running: boolean;

    /**
     * Start marquee
     */
    start(): boolean;

    /**
     * Stop marquee
     */
    stop(): boolean;
}

export interface MarqueeEvents {
    /**
     * Event will be fired in when marquee started
     */
    marqueeStart: (swiper: Swiper) => void;
    /**
     * Event will be fired when marquee stopped
     */
    marqueeStop: (swiper: Swiper) => void;
    /**
     * Event will be fired when slide changed with marquee
     */
    marquee: (swiper: Swiper) => void;
}

export interface MarqueeOptions {
    enabled: boolean;
    /**
     * When enabled marquee will not be stopped when it reaches last slide. It will run in reverse direction.
     *
     * @default false
     */
    bounce?: boolean;
    /**
     * When enabled marquee will wait for wrapper transition to continue.
     * Can be disabled in case of using Virtual Translate when your
     * slider may not have transition
     *
     * @default true
     */
    waitForTransition?: boolean;
    /**
     * Set to `false` and marquee will not be disabled after user interactions (swipes),
     * it will be restarted every time after interaction
     *
     * @default false
     */
    disableOnInteraction?: boolean;
    /**
     * Enable this parameter and marquee will be stopped when it reaches last slide (has no effect in loop mode)
     *
     * @default false
     */
    stopOnLastSlide?: boolean;
    /**
     * Enables marquee in reverse direction
     *
     * @default false
     */
    reverseDirection?: boolean;
    /**
     * When enabled marquee will be paused on mouse enter over Swiper container.
     * If `disableOnInteraction` is also enabled, it will stop marquee instead of pause
     *
     * @default true
     */
    pauseOnMouseEnter?: boolean;
    watchSlidesProgress?: boolean;
    /**
     * Marquee effect speed in `px`.
     * Value `1` is equal and 1px per request animation frame (60 times per second).
     * Between `0` and `1` it is slower, more then `1` it is faster
     *
     * @default 1
     */
    speed?: number;
}

export enum MarqueeDirection {
    Prev = 'prev',
    Next = 'next'
}

declare module 'swiper/types' {
    interface Swiper {
        marquee: Partial<MarqueeMethods> & {
            initialized: boolean;
            running: boolean;
            paused: boolean;
            direction: MarqueeDirection;
        };
    }

    interface SwiperEvents {
        marqueeStart: (swiper: Swiper) => void;
        marqueeStop: (swiper: Swiper) => void;
        marqueePause: (swiper: Swiper) => void;
        marqueeResume: (swiper: Swiper) => void;
        marquee: (swiper: Swiper) => void;
    }

    interface SwiperOptions {
        marquee?: boolean | MarqueeOptions;
    }
}

export function Marquee({ swiper, extendParams, on, emit }) {
    let _requestAnimationFrame: number | undefined;

    swiper.marquee = {
        initialized: false,
        running: false,
        paused: false,
        direction: MarqueeDirection.Next
    };

    extendParams({
        marquee: {
            enabled: false,
            bounce: false,
            waitForTransition: true,
            disableOnInteraction: false,
            stopOnLastSlide: false,
            reverseDirection: false,
            pauseOnMouseEnter: true,
            watchSlidesProgress: true,
            speed: 1
        }
    });

    const run = () => {
        if (!swiper.enabled || !swiper.marquee.running || swiper.marquee.paused || swiper.width >= swiper.virtualSize) {
            return;
        }

        if (_requestAnimationFrame) {
            cancelAnimationFrame(_requestAnimationFrame);
        }

        _requestAnimationFrame = requestAnimationFrame(() => {
            if (swiper.marquee.direction === MarqueeDirection.Prev) {
                if (!swiper.isBeginning) {
                    emit('marquee');
                } else if (!swiper.params.marquee.stopOnLastSlide) {
                    if (swiper.params.marquee.bounce) {
                        swiper.marquee.direction = MarqueeDirection.Next;
                    } else if (swiper.params.loop) {
                        swiper.slideToLoop(swiper.slides.length - 1, 0, true);
                    } else {
                        swiper.slideTo(swiper.slides.length - 1, 0, true);
                    }

                    emit('marquee');
                } else {
                    stop();
                }
            } else if (!swiper.isEnd) {
                emit('marquee');
            } else if (!swiper.params.marquee.stopOnLastSlide) {
                if (swiper.params.marquee.bounce) {
                    swiper.marquee.direction = MarqueeDirection.Prev;
                } else if (swiper.params.loop) {
                    swiper.slideToLoop(0, 0, true);
                } else {
                    swiper.slideTo(0, 0, true);
                }

                emit('marquee');
            } else {
                stop();
            }

            const update = () => {
                const speed = swiper.params.marquee.speed || 1;
                const diff = swiper.marquee.direction === MarqueeDirection.Prev ? speed : -1 * speed;

                swiper.swipeDirection = swiper.marquee.direction;
                const translate = diff + swiper.getTranslate();

                // Update progress
                swiper.updateProgress(translate);
                // Update translate
                swiper.setTranslate(translate);

                swiper.transitionStart(true, swiper.swipeDirection);

                // Update active index in free mode
                if (
                    (swiper.params.freeMode && swiper.params.freeMode.enabled && swiper.freeMode) ||
                    swiper.params.watchSlidesProgress ||
                    swiper.params.marquee.watchSlidesProgress
                ) {
                    swiper.updateActiveIndex();
                    swiper.updateSlidesClasses();
                }

                run();
            };

            update();
        });
    };

    const start = () => {
        if (!swiper.enabled) {
            return false;
        }

        if (typeof _requestAnimationFrame !== 'undefined') {
            return false;
        }

        if (swiper.marquee.running) {
            return false;
        }

        swiper.marquee.running = true;

        emit('marqueeStart');
        run();

        return true;
    };

    const stop = () => {
        if (!swiper.marquee.running) {
            return false;
        }

        if (typeof _requestAnimationFrame === 'undefined') {
            return false;
        }

        if (_requestAnimationFrame) {
            cancelAnimationFrame(_requestAnimationFrame);
            _requestAnimationFrame = undefined;
        }

        swiper.marquee.running = false;

        emit('marqueeStop');

        return true;
    };

    const pause = (speed?: number) => {
        if (!swiper.marquee.running) {
            return;
        }

        if (swiper.marquee.paused) {
            return;
        }

        if (_requestAnimationFrame) {
            cancelAnimationFrame(_requestAnimationFrame);
            _requestAnimationFrame = undefined;
        }

        swiper.marquee.paused = true;

        if (speed === 0 || !swiper.params.marquee.waitForTransition) {
            swiper.marquee.paused = false;
            run();
        } else {
            ['transitionend', 'webkitTransitionEnd'].forEach(event => {
                swiper.wrapperEl.addEventListener(event, onTransitionEnd);
            });
        }
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden' && swiper.marquee.running) {
            pause();
        }

        if (document.visibilityState === 'visible' && swiper.marquee.paused) {
            swiper.marquee.paused = false;
            run();
        }
    };

    const onTransitionEnd = (e: TransitionEvent) => {
        if (!swiper || swiper.destroyed || !swiper.wrapperEl) {
            return;
        }

        if (e.target !== swiper.wrapperEl[0]) {
            return;
        }

        ['transitionend', 'webkitTransitionEnd'].forEach(event => {
            swiper.wrapperEl.removeEventListener(event, onTransitionEnd);
        });

        swiper.marquee.paused = false;

        if (!swiper.marquee.running) {
            stop();
        } else {
            run();
        }
    };

    const onMouseEnter = () => {
        if (swiper.params.marquee.disableOnInteraction) {
            stop();
        } else {
            emit('marqueePause');
            pause();
        }

        ['transitionend', 'webkitTransitionEnd'].forEach(event => {
            swiper.wrapperEl.removeEventListener(event, onTransitionEnd);
        });
    };

    const onMouseLeave = () => {
        if (swiper.params.marquee.disableOnInteraction) {
            return;
        }

        swiper.marquee.paused = false;

        emit('marqueeResume');
        run();
    };

    const attachMouseEvents = () => {
        if (swiper.params.marquee.pauseOnMouseEnter) {
            swiper.el.addEventListener('mouseenter', onMouseEnter);
            swiper.el.addEventListener('mouseleave', onMouseLeave);
        }
    };

    const detachMouseEvents = () => {
        if (swiper.el && typeof swiper.el !== 'string') {
            swiper.el.removeEventListener('mouseenter', onMouseEnter);
            swiper.el.removeEventListener('mouseleave', onMouseLeave);
        }
    };

    const init = () => {
        swiper.marquee.direction = swiper.params.marquee.reverseDirection ? MarqueeDirection.Prev : MarqueeDirection.Next;

        start();

        document.addEventListener('visibilitychange', onVisibilityChange);

        attachMouseEvents();

        swiper.marquee.initialized = true;
    };

    on('beforeInit', () => {
        if (swiper.params.marquee.enabled) {
            const overwriteParams = {
                freeMode: true,
                watchSlidesProgress: true
            };

            Object.assign(swiper.params, overwriteParams);
            Object.assign(swiper.originalParams, overwriteParams);
        }
    });

    on('init', () => {
        if (swiper.params.marquee.enabled) {
            init();
        }
    });

    on('update', () => {
        if (swiper.params.marquee && swiper.params.marquee.enabled && !swiper.marquee.initialized) {
            init();
        }
    });

    on('reachBeginning', () => {
        if (swiper.marquee.direction === MarqueeDirection.Next) {
            onMouseLeave();
        }
    });

    on('reachEnd', () => {
        if (swiper.marquee.direction === MarqueeDirection.Prev) {
            onMouseLeave();
        }
    });

    on('beforeTransitionStart', (_s: Swiper, speed?: number, internal?: boolean) => {
        if (swiper.marquee.running) {
            if (internal || !swiper.params.marquee.disableOnInteraction) {
                pause(speed);
            } else {
                stop();
            }
        }
    });

    on('sliderFirstMove', () => {
        if (swiper.marquee.running) {
            if (swiper.params.marquee.disableOnInteraction) {
                stop();
            } else {
                pause();
            }
        }
    });

    on('touchEnd', () => {
        if (swiper.params.cssMode && swiper.marquee.paused && !swiper.params.marquee.disableOnInteraction) {
            run();
        }
    });

    on('destroy', () => {
        detachMouseEvents();

        if (swiper.marquee.running) {
            stop();
        }

        document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    Object.assign(swiper.marquee, {
        pause,
        run,
        start,
        stop
    });
}
